/**
 * Research Status Store
 * Manages polling-based status tracking for research and orchestration requests
 *
 * Migrated from SSE to polling for reliability across network configurations.
 * Backend endpoint: GET /api/chat/status/:requestId (Redis-backed)
 */
import { create } from 'zustand'
import { chatApi } from '../../lib/api'
import type { ResearchPhase, ResearchStatus, ResearchAction } from './types'
import { logger } from '@/lib/logger'

// Polling interval configuration (exponential backoff)
const INITIAL_POLLING_INTERVAL_MS = 500  // Start at 500ms
const MAX_POLLING_INTERVAL_MS = 2000     // Max out at 2s
const BACKOFF_MULTIPLIER = 1.5           // Increase by 50% each poll

// Maximum polling duration (5 minutes)
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000

// Stop polling after this many consecutive 404s (status was never created for this request)
// Raised from 3 to 8: backend needs time for auth + complexity classification + model selection
// before setting status in Redis. With 500ms initial interval + 1.5x backoff, 8 retries ≈ 12s.
const MAX_CONSECUTIVE_NOT_FOUND = 8

// Delay before first poll to give backend time to set status in Redis.
// Reduced 1500→500: emitPreparingQuery writes the initial Redis status within
// ~200–300ms of the request arriving, so 1500ms was 5× too conservative and
// left the status indicator invisible for 1.5–2s. MAX_CONSECUTIVE_NOT_FOUND=8
// still backs off gracefully if the status isn't ready on the first poll.
const INITIAL_POLL_DELAY_MS = 500

interface ResearchStatusState {
  researchStatus: ResearchStatus | null
  pollingTimeoutId: ReturnType<typeof setTimeout> | null
  currentPollingInterval: number

  subscribeToResearchStatus: (requestId: string, isResearchMode?: boolean) => void
  clearResearchStatus: () => void
  reset: () => void
}

/**
 * Map backend phase to frontend display phase
 * For orchestration mode (non-research), 'searching' phase is skipped
 */
function mapBackendPhase(phase: string, mode: 'research' | 'orchestration' = 'research'): ResearchPhase {
  switch (phase) {
    case 'initializing': {
      return 'initializing'
    }
    case 'searching': {
      // For orchestration mode, there's no web search - map to 'analyzing'
      return mode === 'orchestration' ? 'analyzing' : 'searching'
    }
    case 'analyzing': {
      return 'analyzing'
    }
    case 'synthesizing': {
      return 'synthesizing'
    }
    case 'finalizing': {
      return 'finalizing'
    }
    case 'complete': {
      return 'complete'
    }
    case 'error': {
      return 'error'
    }
    default: {
      return mode === 'orchestration' ? 'analyzing' : 'searching'
    }
  }
}

/**
 * Map backend phase and action to provider status
 * Uses action to distinguish between "model responding" vs "model completed"
 */
function mapProviderStatus(phase: string, action?: string): 'pending' | 'searching' | 'complete' | 'error' {
  // If action is 'failed', the model encountered an error
  if (action === 'failed') {
    return 'error'
  }
  // If action is 'receiving' or 'completed', the model has finished responding
  if (action === 'receiving' || action === 'completed') {
    return 'complete'
  }

  switch (phase) {
    case 'initializing': {
      return 'pending'
    }
    case 'searching': {
      return 'searching'
    }
    case 'analyzing': {
      return 'searching'
    } // Still in progress during analysis
    case 'synthesizing':
    case 'complete': {
      return 'complete'
    }
    case 'error': {
      return 'error'
    }
    default: {
      return 'searching'
    }
  }
}

export const useResearchStatusStore = create<ResearchStatusState>((set, get) => ({
  researchStatus: null,
  pollingTimeoutId: null,
  currentPollingInterval: INITIAL_POLLING_INTERVAL_MS,

  /**
   * Subscribe to research status updates via polling with exponential backoff
   *
   * Polls the backend status endpoint starting at 500ms intervals, increasing
   * to 2s max to reduce server load (from 120 req/min to ~30-60 req/min).
   *
   * @param requestId - Unique identifier for this request
   * @param isResearchMode - Selects the status mode ('research' vs 'orchestration').
   *   Both modes now seed an immediate 'initializing' status (Fix 2); the first poll
   *   then replaces it with per-model provider rows.
   */
  subscribeToResearchStatus: (requestId: string, isResearchMode?: boolean) => {
    // Clear any existing polling
    const existingTimeout = get().pollingTimeoutId
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    const mode: 'research' | 'orchestration' = isResearchMode ? 'research' : 'orchestration'

    // Initialize an "initializing" status immediately for BOTH modes so the UI
    // gives feedback the moment a message is sent (previously orchestration mode
    // showed nothing until the first poll returned ~1.5–2s later). The first poll
    // then replaces this with per-model provider rows.
    set({
      researchStatus: {
        requestId,
        phase: 'initializing',
        mode,
        providers: []
      },
      pollingTimeoutId: null,
      currentPollingInterval: INITIAL_POLLING_INTERVAL_MS
    })

    const startTime = Date.now()
    let consecutiveNotFoundCount = 0

    // Poll function with exponential backoff
    const poll = async () => {
      try {
        // Check timeout
        if (Date.now() - startTime > MAX_POLLING_DURATION_MS) {
          logger.warn('Research status polling timeout - clearing status after 5 minutes')
          set({ researchStatus: null, pollingTimeoutId: null, currentPollingInterval: INITIAL_POLLING_INTERVAL_MS })
          return
        }

        const response = await chatApi.getStatus(requestId)

        // Reset not-found counter on any successful response
        consecutiveNotFoundCount = 0

        if (!response.success || !response.data) {
          scheduleNextPoll()
          return
        }

        const status = response.data
        const latestEntry = status.phaseHistory[status.phaseHistory.length - 1]

        if (!latestEntry) {
          scheduleNextPoll()
          return
        }

        // Extract latest status info
        const eventMode = latestEntry.mode || mode
        const displayPhase = mapBackendPhase(latestEntry.phase, eventMode)

        // Build providers list from phase history
        const providersMap = new Map<string, {
          provider: string
          status: 'pending' | 'searching' | 'complete' | 'error'
          message?: string
          sourceCount?: number
        }>()

        // Process all history entries to build current provider states
        for (const entry of status.phaseHistory) {
          if (entry.provider && entry.provider !== 'system') {
            providersMap.set(entry.provider, {
              provider: entry.provider,
              status: mapProviderStatus(entry.phase, entry.action),
              message: entry.message,
              sourceCount: entry.sourceCount
            })
          }
        }

        const providers = [...providersMap.values()]

        set({
          researchStatus: {
            requestId,
            phase: displayPhase,
            mode: eventMode,
            providers,
            action: latestEntry.action as ResearchAction | undefined,
            actionDetail: latestEntry.actionDetail,
            queryCount: latestEntry.queryCount,
            processingStep: latestEntry.processingStep
          }
        })

        // Stop polling if complete or error
        if (displayPhase === 'complete' || displayPhase === 'error') {
          set({ pollingTimeoutId: null, currentPollingInterval: INITIAL_POLLING_INTERVAL_MS })

          // For complete phase, clear status after fade-out animation
          if (displayPhase === 'complete') {
            setTimeout(() => {
              set({ researchStatus: null })
            }, 700)
          } else {
            // Error - clear immediately
            set({ researchStatus: null })
          }
        } else {
          // Continue polling with backoff
          scheduleNextPoll()
        }
      } catch (error) {
        // Check if this is a 404 "not found" error (backend never created status for this request)
        const isNotFound = error instanceof Error && error.message.includes('not found')
        if (isNotFound) {
          consecutiveNotFoundCount++
          if (consecutiveNotFoundCount >= MAX_CONSECUTIVE_NOT_FOUND) {
            // Backend won't create status for this request — stop polling silently
            set({ researchStatus: null, pollingTimeoutId: null, currentPollingInterval: INITIAL_POLLING_INTERVAL_MS })
            return
          }
          // Still waiting for backend to create status — retry with backoff
          scheduleNextPoll()
        } else {
          logger.error('Research status polling error', error)
          // Don't stop polling on transient errors - schedule next poll with backoff
          scheduleNextPoll()
        }
      }
    }

    // Schedule next poll with exponential backoff
    const scheduleNextPoll = () => {
      const currentInterval = get().currentPollingInterval
      const nextInterval = Math.min(currentInterval * BACKOFF_MULTIPLIER, MAX_POLLING_INTERVAL_MS)

      const timeoutId = setTimeout(poll, currentInterval)
      set({
        pollingTimeoutId: timeoutId,
        currentPollingInterval: nextInterval
      })
    }

    // Start polling after a short delay to give the backend time to set initial status
    // Without this delay, the first few polls get 404 and polling may stop prematurely
    const initialTimeout = setTimeout(poll, INITIAL_POLL_DELAY_MS)
    set({ pollingTimeoutId: initialTimeout })
  },

  /**
   * Clear research status and stop polling
   */
  clearResearchStatus: () => {
    const timeoutId = get().pollingTimeoutId
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    set({
      researchStatus: null,
      pollingTimeoutId: null,
      currentPollingInterval: INITIAL_POLLING_INTERVAL_MS
    })
  },

  /**
   * Reset store state (used on logout)
   */
  reset: () => {
    const timeoutId = get().pollingTimeoutId
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    set({
      researchStatus: null,
      pollingTimeoutId: null,
      currentPollingInterval: INITIAL_POLLING_INTERVAL_MS
    })
  }
}))
